import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Box, CircularProgress, LinearProgress } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import PDFPageSelectionControls from './pdf-page-selection-controls/PDFPageSelectionControls';
import { PDFViewerTabProps } from './PDFViewerTab.props';
import { ApiCreateDocumentGenerationJob, ApiService, FileModel } from '../../../../api';
import { RetrieveDocumentGenerationJob } from '../../../../api/models/RetrieveDocumentGenerationJob';
import { EventType, useEventSubscription } from '../../../../context';
import { useLayoutContext } from '../../../../context/LayoutContext';
import { GenerationJobParsingMode } from '../../../../pages/DocumentGenerationJobs/models/enums/generation-job-parsing-mode.enum';
import { GenerationJobState } from '../../../../pages/DocumentGenerationJobs/models/enums/generation-job-state.enum';
import { useDocuments } from '../../../../services/hooks';
import { Logger } from '../../../../utilities';
import { PageData, PDFCitationPayload } from '../../../FileViewer/pdf-viewer/pdf-utils/constants';
import PDFViewer from '../../../FileViewer/pdf-viewer/PDFViewer';
import { PDFViewerViewMode } from '../../../FileViewer/pdf-viewer/PDFViewer.props';
import { DEFAULT_DOC_CONTENT_STALE_TIME_IN_MS } from '../document-preview.constants';
import { PDFJobRibbon } from './pdf-job-ribbon/PDFJobRibbon';
import { useCitationContext } from '../../../../pages/CapTable/cap-table/components/captable-table/CitationContext';
import { useAttributeMappedWidgetDrawer } from '../../attribute-mapped-widget-drawer/AttributeMappedWidgetDrawerContext';

const PDFViewerTab = ({ document, companyId }: PDFViewerTabProps) => {
  const { apiCompanyDocumentGenerationJobsNewCreate } = ApiService;
  const { currentMeasurementDate } = useLayoutContext();
  const { downloadDocumentContent } = useDocuments();
  const [selectedPages, setSelectedPages] = useState<Set<number>>(new Set());
  const [allowPageSelection, setAllowPageSelection] = useState(false);
  const [processingJob, setProcessingJob] = useState<RetrieveDocumentGenerationJob>();
  const [renderingJob, setRenderingJob] = useState<RetrieveDocumentGenerationJob>();
  const [genJobProcessing, setGenJobProcessing] = useState(false);
  const [renderJobProcessing, setRenderJobProcessing] = useState(false);
  const [jsonData, setJsonData] = useState<PageData[] | null>(null);

  const { activeCitation, setActiveCitation } = useCitationContext();
  const { onCitationPushed } = useAttributeMappedWidgetDrawer();

  // Clear active citation when the spreadsheet active cell changes
  useEventSubscription(EventType.SPREADSHEET_ACTIVE_CELL_CHANGED, () => {
    setActiveCitation();
  });

  // Clear active citation when this document changes
  useEffect(() => {
    setActiveCitation();
  }, [setActiveCitation, document.document_reference_id]);

  // ESC to dismiss active citation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && activeCitation) setActiveCitation();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [activeCitation, setActiveCitation]);

  const { data, isLoading } = useQuery({
    queryKey: ['document', document.document_reference_id, 'content'],
    enabled: !!document.document_reference_id,
    staleTime: DEFAULT_DOC_CONTENT_STALE_TIME_IN_MS,
    queryFn: () => downloadDocumentContent(document),
  });

  // Fetch parser results JSON if json_file_path is set on the file
  const { data: parserResultsData } = useQuery({
    queryKey: ['document', document.document_reference_id, 'parser-results'],
    enabled: !!document.file?.json_file_path,
    staleTime: Infinity,
    queryFn: () => ApiService.apiDocumentsReferencesParserResultsRead(document.document_reference_id.toString()),
  });

  useEffect(() => {
    if (!parserResultsData?.url) return;
    fetch(parserResultsData.url)
      .then(r => r.json())
      .then((data: PageData[]) => setJsonData(data))
      .catch(err => Logger.error('Failed to fetch parser results JSON:', err));
  }, [parserResultsData]);

  const handleCitationPushed = useCallback(
    (payload: PDFCitationPayload) => {
      onCitationPushed?.(payload, document.document_reference_id);
    },
    [onCitationPushed, document.document_reference_id]
  );

  const handlePageToggle = (pageNumber: number) => {
    setSelectedPages(prev => {
      const newSelected = new Set(prev);
      if (newSelected.has(pageNumber)) {
        newSelected.delete(pageNumber);
      } else {
        newSelected.add(pageNumber);
      }
      return newSelected;
    });
  };

  const handleProcessDocument = useCallback(
    async (isTableExtraction: boolean = true) => {
      if (!document.file?.id || !companyId) {
        Logger.warn('Missing required data: file ID or company ID');
        return;
      }

      try {
        if (isTableExtraction) {
          setGenJobProcessing(true);
        } else {
          setRenderJobProcessing(true);
        }

        const parserOverrides = isTableExtraction
          ? [
            {
              file_type: FileModel.file_type.PDF,
              page_numbers: Array.from(selectedPages),
              pdf_parsing_mode: GenerationJobParsingMode.TABULAR,
              convert_parsed_results_to_xlsx: true,
            },
          ]
          : [
            {
              file_type: FileModel.file_type.PDF,
              page_numbers: [],
              pdf_parsing_mode: GenerationJobParsingMode.CITABLE,
              convert_parsed_results_to_xlsx: false,
            },
          ];

        const procType = {
          'Cap Table': ApiCreateDocumentGenerationJob.processing_type.CAP_TABLE,
          Financials: ApiCreateDocumentGenerationJob.processing_type.FINANCIALS,
        };
        const processingType = procType[document.referenced_in?.[0]?.reference_object_type as keyof typeof procType];

        if (!processingType) {
          Logger.error('Invalid processing type:', document.referenced_in?.[0]?.reference_object_type);
          return;
        }

        const results = await apiCompanyDocumentGenerationJobsNewCreate(companyId, {
          reference_files: [document.file.id],
          processing_type: processingType,
          desired_termination_state: GenerationJobState.PENDING_PROMPTING,
          parser_overrides: parserOverrides,
        });

        if (isTableExtraction) {
          setProcessingJob(results);
        } else {
          setRenderingJob(results);
        }
      } catch (error) {
        Logger.error(`Error ${isTableExtraction ? 'processing pages' : 'rendering document'}:`, error);
        if (isTableExtraction) {
          setGenJobProcessing(false);
        } else {
          setRenderJobProcessing(false);
        }
      }
    },
    [apiCompanyDocumentGenerationJobsNewCreate, companyId, document.file?.id, selectedPages, document.referenced_in]
  );

  const handleProcessPages = useCallback(() => handleProcessDocument(true), [handleProcessDocument]);
  const handleRenderDocument = useCallback(() => handleProcessDocument(false), [handleProcessDocument]);

  const handleClearSelection = () => {
    setSelectedPages(new Set());
  };

  const toggleSelectionMode = (enabled: boolean) => {
    setAllowPageSelection(enabled);
    if (!enabled) {
      setSelectedPages(new Set());
    }
  };

  const getReferenceObjectTypeCode = (displayType?: string): string => {
    const typeMap: Record<string, string> = {
      'Cap Table': 'ct',
      Financials: 'fi',
      'Fund Ownership': 'fo',
      'Custom Breakpoints': 'cb',
      'External Value': 'ev',
      'Specified Share Values': 'ssv',
      'Fund Investment Ownership Summary': 'fio',
      'Market Adjustment': 'ma',
    };

    if (displayType && displayType.length <= 3 && displayType === displayType.toLowerCase()) {
      return displayType;
    }

    return typeMap[displayType || ''] || 'ct';
  };

  const referenceObjectType = getReferenceObjectTypeCode(document.referenced_in?.[0]?.reference_object_type);

  const autoCreationAttempted = useRef(false);

  useEffect(() => {
    autoCreationAttempted.current = false;
  }, [document.document_reference_id]);

  useEffect(() => {
    if (
      referenceObjectType === 'ct' &&
      !document.file?.generation_job &&
      !autoCreationAttempted.current &&
      !renderingJob &&
      !renderJobProcessing
    ) {
      autoCreationAttempted.current = true;
      handleRenderDocument();
    }
  }, [referenceObjectType, document.file?.generation_job, renderingJob, renderJobProcessing, handleRenderDocument]);

  const currentDocumentHasActiveCitation = useMemo(() => activeCitation?.document_reference === document.document_reference_id, [activeCitation, document]);

  if (isLoading) {
    return <LinearProgress />;
  }

  if (!data.content && !data.blob_url) {
    return <div>No document found: Current Document Preview size limit is 5MB</div>;
  }

  return (
    <Box>
      {processingJob && currentMeasurementDate && (
        <PDFJobRibbon
          generationJob={processingJob}
          measurementDate={currentMeasurementDate}
          referenceObjectType={referenceObjectType}
        />
      )}

      {renderJobProcessing && !renderingJob && (
        <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0' }}>
          <Alert severity="info" icon={<CircularProgress size={20} />} sx={{ display: 'flex', alignItems: 'center' }}>
            Preparing document for citations...
          </Alert>
        </Box>
      )}

      {renderingJob && currentMeasurementDate && (
        <PDFJobRibbon
          generationJob={renderingJob}
          measurementDate={currentMeasurementDate}
          referenceObjectType={referenceObjectType}
        />
      )}

      <PDFPageSelectionControls
        allowPageSelection={allowPageSelection}
        selectedPages={selectedPages}
        onToggleSelectionMode={toggleSelectionMode}
        onPageToggle={handlePageToggle}
        onClearSelection={handleClearSelection}
        onProcessPages={handleProcessPages}
        genJobProcessing={genJobProcessing}
      />

      <PDFViewer
        base64Content={data.content}
        src={data.blob_url}
        allowPageSelection={allowPageSelection}
        selectedPages={selectedPages}
        viewMode={allowPageSelection ? PDFViewerViewMode.THUMBNAIL : PDFViewerViewMode.DOCUMENT}
        onPageToggle={handlePageToggle}
        jsonData={jsonData ?? undefined}
        activeCitation={currentDocumentHasActiveCitation ? activeCitation : undefined}
        onCitationPushed={handleCitationPushed}
      />
    </Box>
  );
};

export default PDFViewerTab;
